import { TenantAboutPage, TenantConfig } from './tenant.model';

export const ABOUT_MAX_BYTES = 16 * 1024;
export const ABOUT_LIMITS = Object.freeze({ introduction: 1500, sections: 3, heading: 100, paragraphs: 2, paragraph: 2000 });

export type TenantAboutMode = 'neutral' | 'configured' | 'configured-fallback';

export interface TenantAboutSectionView {
  heading: string;
  paragraphs: string[];
  imageUrl: string;
}

export interface TenantAboutView {
  mode: TenantAboutMode;
  configured: boolean;
  introduction: string;
  sections: TenantAboutSectionView[];
}

function compact(value: unknown): string {
  return String(value ?? '').replace(/\r\n?/g, '\n').trim();
}

function safeAssetUrl(value: unknown): string {
  const raw = compact(value);
  return /^(https?:)?\/\//i.test(raw) || raw.startsWith('/') || /^(assets|\/assets)\//i.test(raw) ? raw : '';
}

export function neutralAboutPage(leagueName: string): TenantAboutPage {
  const name = compact(leagueName) || 'WL NXT LEVEL';
  return {
    configured: false,
    introduction: `Welcome to ${name}, where the love of basketball meets organized recreational competition. We bring players together through competitive games, clear league operations, and a community built around the sport.`,
    sections: [
      {
        heading: 'Our Story',
        paragraphs: [`${name} gives teams and players a structured place to compete, track progress, and build a season around meaningful games. Our platform supports league operations from registration through scheduling, standings, stats, and payments.`],
      },
      {
        heading: 'our mission',
        paragraphs: [`At ${name}, our mission is to create a reliable and inclusive basketball experience for players of different backgrounds and skill levels. We aim to make each season organized, competitive, and easy to follow for players, captains, and fans.`],
      },
      {
        heading: 'What sets us apart',
        paragraphs: [
          `${name} uses digital tools for registration, schedules, player statistics, payments, and league administration so players and organizers can spend more time focused on the game.`,
          `${name} connects basketball players through clear communication, consistent rules, and a league experience that can grow with each community it serves.`,
        ],
      },
    ],
  };
}

export function cloneAboutPage(value: TenantAboutPage): TenantAboutPage {
  return {
    configured: value.configured === true,
    introduction: value.introduction,
    sections: value.sections.map((section) => ({
      heading: section.heading,
      paragraphs: [...section.paragraphs],
      ...(section.image_url ? { image_url: section.image_url } : {}),
    })),
  };
}

function validConfiguredAbout(value: TenantAboutPage | undefined): value is TenantAboutPage {
  if (!value || value.configured !== true || typeof value.introduction !== 'string' || !compact(value.introduction)) return false;
  if (!Array.isArray(value.sections) || value.sections.length !== ABOUT_LIMITS.sections) return false;
  return value.sections.every((section) =>
    section && typeof section.heading === 'string' && Boolean(compact(section.heading)) && compact(section.heading).length <= ABOUT_LIMITS.heading &&
    Array.isArray(section.paragraphs) && section.paragraphs.length >= 1 && section.paragraphs.length <= ABOUT_LIMITS.paragraphs &&
    section.paragraphs.every((paragraph) => typeof paragraph === 'string' && Boolean(compact(paragraph)) && compact(paragraph).length <= ABOUT_LIMITS.paragraph)
  );
}

export function resolveAboutPage(tenant: TenantConfig | null, leagueName: string): TenantAboutView {
  const hero = safeAssetUrl(tenant?.branding?.hero_image_url);
  const stored = tenant?.about;
  if (stored?.configured === true) {
    const base = validConfiguredAbout(stored) ? cloneAboutPage(stored) : neutralAboutPage(leagueName);
    return {
      mode: validConfiguredAbout(stored) ? 'configured' : 'configured-fallback',
      configured: true,
      introduction: compact(base.introduction),
      sections: base.sections.map((section) => ({
        heading: compact(section.heading),
        paragraphs: section.paragraphs.map(compact),
        imageUrl: safeAssetUrl(section.image_url) || hero,
      })),
    };
  }
  const base = neutralAboutPage(leagueName);
  return {
    mode: 'neutral',
    configured: false,
    introduction: base.introduction,
    sections: base.sections.map((section) => ({
      heading: section.heading,
      paragraphs: [...section.paragraphs],
      imageUrl: hero,
    })),
  };
}
